import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, LogIn } from "lucide-react";
import { LOGO_DARK, LOGO_LIGHT, NAV_LINKS, QUOTE_URL, CLIENT_PORTAL_URL } from "../data/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const overHero = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <motion.header
        data-testid="site-navbar"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color,padding,box-shadow] duration-500 ${
          scrolled
            ? "bg-[hsl(30_40%_97%/0.92)] backdrop-blur-xl border-b border-[hsl(var(--border))] py-3 shadow-[0_2px_20px_rgba(20,18,16,0.06)]"
            : "bg-gradient-to-b from-[hsl(22_26%_10%/0.55)] to-transparent border-b border-transparent py-4"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 flex items-center gap-3 xl:gap-5">
          {/* Logo — same size on hero + scrolled to keep layout stable */}
          <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0" aria-label="Pro DJ's Abilene — Home">
            <img
              src={overHero ? LOGO_LIGHT : LOGO_DARK}
              alt="Pro DJ's Abilene — Wedding DJ, Planning & Decorator"
              width="240"
              height="146"
              fetchPriority="high"
              decoding="async"
              className="h-14 md:h-16 w-auto object-contain transition-opacity duration-500"
              style={{ filter: overHero
                ? "drop-shadow(0 2px 8px rgba(0,0,0,0.45))"
                : "drop-shadow(0 1px 2px rgba(20,18,16,0.15))" }}
            />
          </Link>

          {/* Nav links — centered, 5 items, comfortable spacing */}
          <nav className="hidden xl:flex items-center gap-5 xl:gap-7 mx-auto">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `link-underline text-[0.72rem] xl:text-[0.78rem] uppercase tracking-[0.14em] xl:tracking-[0.16em] font-body transition-colors duration-300 whitespace-nowrap ${
                    overHero
                      ? `nav-shadow ${isActive ? "text-white" : "text-white/90 hover:text-white"}`
                      : isActive
                      ? "text-gold"
                      : "text-espresso hover:text-gold"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* CTAs — ALWAYS visible on desktop (fix for user's #3 complaint) */}
          <div className="hidden xl:flex items-center gap-2 shrink-0">
            <a
              href={CLIENT_PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-client-portal-cta"
              className="group inline-flex items-center gap-1.5 rounded-full bg-gold-gradient text-espresso px-3.5 py-2 text-[0.68rem] xl:text-[0.7rem] uppercase tracking-[0.12em] font-semibold whitespace-nowrap shadow-[0_4px_16px_hsl(38_58%_66%/0.45)] ring-1 ring-[hsl(var(--gold)/0.55)] transition-all duration-300 hover:scale-[1.04] hover:shadow-[0_8px_24px_hsl(38_58%_66%/0.6)]"
            >
              <LogIn size={12} className="opacity-90" />
              Client Login
            </a>
            <a
              href={QUOTE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-quote-cta"
              className={`group inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[0.68rem] xl:text-[0.72rem] uppercase tracking-[0.13em] xl:tracking-[0.15em] font-body whitespace-nowrap transition-all duration-300 hover:scale-[1.04] ${
                overHero
                  ? "bg-white/95 text-espresso ring-1 ring-white/40 shadow-[0_4px_16px_rgba(0,0,0,0.25)]"
                  : "bg-espresso text-[hsl(var(--primary-foreground))]"
              }`}
            >
              Instant Quote
              <ArrowUpRight size={12} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`xl:hidden p-1 ml-auto transition-colors duration-300 ${overHero ? "text-white nav-shadow" : "text-espresso"}`}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-[hsl(30_40%_97%/0.98)] backdrop-blur-lg xl:hidden flex flex-col items-center justify-center gap-7"
          >
            {NAV_LINKS.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i + 0.1 }}
              >
                <NavLink
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `font-serif text-3xl ${isActive ? "text-gold" : "text-espresso"}`
                  }
                  end={l.to === "/"}
                >
                  {l.label}
                </NavLink>
              </motion.div>
            ))}
            <div className="flex flex-col items-center gap-3 mt-4">
              <a
                href={QUOTE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-quote-cta"
                className="inline-flex items-center gap-2 rounded-full bg-espresso text-[hsl(var(--primary-foreground))] px-8 py-3 text-sm uppercase tracking-[0.16em]"
              >
                Instant Quote <ArrowUpRight size={16} />
              </a>
              <a
                href={CLIENT_PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-client-portal-cta"
                className="inline-flex items-center gap-2 rounded-full bg-gold-gradient text-espresso px-8 py-3 text-sm uppercase tracking-[0.16em] font-semibold shadow-[0_4px_14px_hsl(38_58%_66%/0.35)]"
              >
                <LogIn size={15} /> Client Portal Login
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
